/**
 * Crypto helpers for client-side sessionStorage caches.
 *
 * Extracted Feb 2026 from useNoteAutosave.js so the new chart-bundle
 * SWR session-persistence layer can use the same encryption primitives
 * without duplication.  Both consumers derive the AES-256 key from
 * the current JWT signature so:
 *   - data is unreadable to other origins / extensions
 *   - data automatically invalidates on logout (new JWT → new key)
 */
import CryptoJS from 'crypto-js';

/**
 * Derive a stable AES-256 key from the user's JWT.  Hashes the
 * signature portion (third segment) so the key length is uniform
 * and the signing secret itself isn't exposed.
 */
export const deriveEncryptionKey = (token) => {
  if (!token) return 'fallback_key_not_secure';
  const parts = token.split('.');
  if (parts.length === 3) {
    return CryptoJS.SHA256(parts[2]).toString().substring(0, 32);
  }
  return CryptoJS.SHA256(token).toString().substring(0, 32);
};

/**
 * Encrypt arbitrary JSON-serializable data for sessionStorage.
 * Returns the base64 ciphertext string or null on failure.
 */
export const encryptData = (data, key) => {
  try {
    const jsonString = JSON.stringify(data);
    return CryptoJS.AES.encrypt(jsonString, key).toString();
  } catch (e) {
    console.error('[storageCrypto] Encryption failed:', e);
    return null;
  }
};

/**
 * Decrypt sessionStorage data.  Returns null when the ciphertext is
 * unreadable (typically: the user logged in with a different JWT
 * since the data was cached, so the derived key has changed and
 * the old payload can't be decoded).
 */
export const decryptData = (encryptedData, key) => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, key);
    const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
    if (!decryptedString) return null;
    return JSON.parse(decryptedString);
  } catch {
    return null;
  }
};
